import { renderToString } from 'react-dom/server'

import { MarkerType } from '@web/components/Map/Map.constants'
import { DOT_MARKER_SIZE } from '@web/components/Map/New/constant'
import { CustomMarkerIcon, CustomMarkerOption } from '@web/components/Map/New/types'
import { convertToCategoryTagIcon, convertCategoryToMapMarkerIcon } from '@web/libs/category/mapMarker'
import CategoryMarker from '@web/ui/commons/marker/components/CategoryMarker'
import DotMarker from '@web/ui/commons/marker/components/DotMarker'
import DotWithIconMarker from '@web/ui/commons/marker/components/DotWithIconMarker'
import ThumbnailMarker from '@web/ui/commons/marker/components/ThumbnailMarker'

export const getMarkerSize = (markerOption: CustomMarkerOption) => {
  const [width, height] = ((markerOption.icon as naver.maps.HtmlIcon)?.size as naver.maps.SizeArrayLiteral) ?? []
  return { width: width ?? DOT_MARKER_SIZE, height: height ?? DOT_MARKER_SIZE }
}

export const getMarkerIconAnchor = (icon: CustomMarkerIcon | undefined) => {
  if (!icon) return null

  if (typeof icon === 'string') return null

  if (!('anchor' in icon)) return null

  if (!icon.anchor) return null

  if (!Array.isArray(icon.anchor)) return null

  const [width, height] = icon.anchor
  return { width, height }
}

export interface CreateMarkerIconOption {
  selected?: boolean
  name?: string
  text?: string
  thumbnailUrl?: string | null
  type: MarkerType
  categoryList?: string[]
  categoryTag?: string
}

export const createMarkerIcon = ({
  type,
  name,
  selected = false,
  thumbnailUrl,
  categoryList = [],
  categoryTag,
  text,
}: CreateMarkerIconOption): CustomMarkerIcon => {
  /**
   * 타임라인 지도뷰 > 업커밍 마커
   */
  if (type === MarkerType.UPCOMING) {
    if (selected)
      return {
        type: MarkerType.UPCOMING,
        size: [28, 35],
        anchor: [14, 35],
        content: renderToString(<CategoryMarker name={name} text={text} isSingle type="booking" />),
      }

    return {
      type: MarkerType.UPCOMING,
      size: [20, 20],
      anchor: [10, 10],
      content: renderToString(<DotWithIconMarker name={name} text={text} type="booking" />),
    }
  }

  if (type === MarkerType.FOOTPRINT) {
    if (selected)
      return {
        type: MarkerType.FOOTPRINT,
        size: [28, 35],
        anchor: [14, 35],
        content: renderToString(<CategoryMarker name={name} text={text} isSingle type="visited" />),
      }

    return {
      type: MarkerType.FOOTPRINT,
      size: [20, 20],
      anchor: [10, 10],
      content: renderToString(<DotWithIconMarker name={name} text={text} type="visited" />),
    }
  }

  /**
   * 사진을 포함하는 마커
   */
  if (type === MarkerType.THUMBNAIL) {
    return {
      type: MarkerType.THUMBNAIL,
      size: [44, 48],
      anchor: [22, 48],
      content: renderToString(<ThumbnailMarker thumbnailUrl={thumbnailUrl} isSelected={selected} name={name} />),
    }
  }

  /**
   * 사진을 포함하는 마커
   */
  if (type === MarkerType.THUMBNAIL_SMALL) {
    return {
      type: MarkerType.THUMBNAIL_SMALL,
      size: [35.2, 38.4],
      anchor: [17.6, 38.4],
      content: renderToString(
        <ThumbnailMarker thumbnailUrl={thumbnailUrl} isSelected={selected} name={name} typeSmall />,
      ),
    }
  }

  /**
   * 업종을 표시할 수 있는 마커
   */
  if (type === MarkerType.CATEGORY) {
    const MarkerIcon = convertCategoryToMapMarkerIcon({ categoryList })

    return {
      type: MarkerType.CATEGORY,
      size: [28, 35],
      anchor: [14, 35],
      content: renderToString(
        <CategoryMarker markerIcon={MarkerIcon} name={name} isSingle={true} isSelected={selected} />,
      ),
    }
  }

  /**
   * 타임라인 카테고리 마커
   */
  if (type === MarkerType.TIMELINE_CATEGORY) {
    const MarkerIcon = convertToCategoryTagIcon(categoryTag)

    return {
      type: MarkerType.TIMELINE_CATEGORY,
      size: [28, 35],
      anchor: [14, 35],
      content: renderToString(
        <CategoryMarker markerIcon={MarkerIcon} name={name} isSingle={true} isSelected={selected} />,
      ),
    }
  }

  /**
   * 작은 사이즈의 점 마커
   */
  return {
    type: MarkerType.DOT,
    size: [DOT_MARKER_SIZE, DOT_MARKER_SIZE],
    content: renderToString(<DotMarker size={DOT_MARKER_SIZE} />),
  }
}
